// src/engine/implementationPlanGenerator.ts

import { UserAnswer, RoadmapWeek, ImplementationPlan } from '../types';

export function generateImplementationPlan(
  answers: UserAnswer[]
): ImplementationPlan {
  const answerMap = new Map(answers.map(a => [a.questionId, a.value]));

  // Extract profile information
  const orgType = answerMap.get('GS1') as string || 'startup';
  const stage = answerMap.get('GS2') as string || 'planning';
  const timeline = answerMap.get('GS3') as string || '6months';
  const size = answerMap.get('GS4') as string || 'small';
  const cloudProvider = answerMap.get('GS7') as string || 'aws';
  const aiUsage = answerMap.get('GS8') as string || 'no';
  const usesAI = ['yes_clinical', 'yes_admin', 'yes_both'].includes(aiUsage);
  const physicalFacility = answerMap.get('GS35') as string || 'no';
  const hasPhysicalFacility = ['yes', 'hybrid'].includes(physicalFacility);
  const budget = answerMap.get('GS6') as string || 'standard';
  const approach = answerMap.get('GS5') as string || 'diy';

  // Build monthly phases
  const monthlyPhases: RoadmapWeek[] = [];

  // MONTH 1: Foundation (8-10 items)
  monthlyPhases.push(generateMonth1(
    cloudProvider,
    usesAI,
    answerMap.get('GS9') as string[] || [],
    orgType
  ));

  // MONTH 2: Administrative Safeguards (10-12 items)
  monthlyPhases.push(generateMonth2(
    size,
    orgType,
    hasPhysicalFacility
  ));

  // MONTH 3: Technical Safeguards (9-11 items)
  monthlyPhases.push(generateMonth3(
    cloudProvider,
    usesAI,
    answerMap
  ));

  // MONTH 4: Documentation & Audit Prep (5-7 items)
  monthlyPhases.push(generateMonth4(
    approach,
    budget,
    orgType
  ));

  // Calculate summary
  const totalItems = monthlyPhases.reduce((sum, month) => sum + month.tasks.length, 0);
  const estimatedTime = calculateEstimatedTime(totalItems, size, approach);
  const estimatedCost = calculateEstimatedCost(budget, cloudProvider, usesAI);
  const recommendedApproach = getRecommendedApproach(timeline, size, budget, approach);

  // Collect resources
  const resources = getResources(cloudProvider, usesAI, hasPhysicalFacility);

  return {
    organizationProfile: {
      orgType,
      stage,
      timeline,
      size,
      cloudProvider,
      usesAI,
      hasPhysicalFacility,
    },
    summary: {
      totalItems,
      estimatedTime,
      estimatedCost,
      recommendedApproach,
    },
    monthlyPhases,
    resources,
  };
}

// MONTH 1: Foundation
function generateMonth1(
  cloudProvider: string,
  usesAI: boolean,
  aiVendors: string[],
  orgType: string
): RoadmapWeek {
  const tasks: Array<{ task: string; action: string; resources: string[]; estimatedTime: string }> = [];

  // Week 1: BAAs and designation
  if (cloudProvider === 'aws') {
    tasks.push({
      task: 'Sign AWS Business Associate Agreement (BAA)',
      action: 'Go to AWS Account > Support > Create Case > Request "HIPAA BAA". AWS will process in 1-2 business days.',
      resources: ['AWS BAA Request Guide', '§164.308(b)(1)'],
      estimatedTime: '15 minutes (+ 1-2 days AWS processing)',
    });
  } else if (cloudProvider === 'azure') {
    tasks.push({
      task: 'Sign Azure Business Associate Agreement (BAA)',
      action: 'Navigate to Azure Trust Center > Request BAA. Microsoft processes within 3-5 business days.',
      resources: ['Azure BAA Request Guide', '§164.308(b)(1)'],
      estimatedTime: '15 minutes (+ 3-5 days Microsoft processing)',
    });
  } else if (cloudProvider === 'gcp') {
    tasks.push({
      task: 'Sign Google Cloud Business Associate Agreement (BAA)',
      action: 'Go to Google Cloud Console > HIPAA Compliance > Request BAA.',
      resources: ['GCP BAA Request Guide', '§164.308(b)(1)'],
      estimatedTime: '15 minutes (+ 2-3 days Google processing)',
    });
  }

  tasks.push({
    task: 'Designate HIPAA Security Official',
    action: 'Assign role (typically CTO or senior technical leader), document in writing with responsibilities.',
    resources: ['Security Official Designation Template', '§164.308(a)(2) - REQUIRED'],
    estimatedTime: '30 minutes',
  });

  // Week 2: MFA and AI BAAs
  tasks.push({
    task: 'Enable Multi-Factor Authentication (MFA) for all users',
    action: cloudProvider === 'aws'
      ? 'AWS IAM > Enable MFA > Enforce via IAM policy for all users'
      : 'Configure MFA in your identity provider (Azure AD, Google Workspace, etc.)',
    resources: [
      cloudProvider === 'aws' ? 'AWS MFA Setup Guide' : `${cloudProvider.toUpperCase()} MFA Guide`,
      '2026 HIPAA Update - MFA Now REQUIRED'
    ],
    estimatedTime: '2-4 hours (setup + user onboarding)',
  });

  if (usesAI && aiVendors.length > 0) {
    const vendors = aiVendors.map(v => {
      if (v === 'aws_bedrock') return 'AWS Bedrock';
      if (v === 'azure_openai') return 'Azure OpenAI';
      if (v === 'openai') return 'OpenAI';
      if (v === 'google') return 'Google Vertex AI';
      if (v === 'anthropic') return 'Anthropic Claude';
      return v;
    });

    tasks.push({
      task: `Sign BAAs with AI vendors: ${vendors.join(', ')}`,
      action: 'Contact each AI vendor to execute Business Associate Agreement before using their services with PHI.',
      resources: [
        'BAA Request Email Template',
        'AI Vendor BAA Requirements',
        '§164.308(b)(1)'
      ],
      estimatedTime: '15 minutes per vendor (+ vendor processing time)',
    });
  }

  // Week 3: Encryption
  tasks.push({
    task: 'Enable encryption at rest for all PHI storage',
    action: cloudProvider === 'aws'
      ? 'S3: Enable default encryption (KMS). RDS: Enable encryption at creation. DynamoDB: Enable KMS encryption.'
      : `Configure encryption in ${cloudProvider.toUpperCase()} storage services`,
    resources: [
      cloudProvider === 'aws' ? 'AWS KMS Setup Guide' : `${cloudProvider.toUpperCase()} Encryption Guide`,
      'S3 Encryption Tutorial',
      '§164.312(a)(2)(iv) - Encryption (Addressable)'
    ],
    estimatedTime: '4-6 hours',
  });

  tasks.push({
    task: 'Enable audit logging (CloudTrail, CloudWatch, etc.)',
    action: cloudProvider === 'aws'
      ? 'Enable AWS CloudTrail for all regions. Configure CloudWatch Logs. Set 6-year retention.'
      : 'Configure audit logging in your cloud provider with 6-year retention.',
    resources: [
      cloudProvider === 'aws' ? 'CloudTrail Setup Guide' : `${cloudProvider.toUpperCase()} Logging Guide`,
      '§164.312(b) - Audit Controls (REQUIRED)'
    ],
    estimatedTime: '3-4 hours',
  });

  // Week 4: Documentation start
  tasks.push({
    task: 'Start HIPAA risk analysis template',
    action: 'Download NIST risk analysis template. Identify all systems that store/process PHI. Document assets and threats. (Will complete in Month 2)',
    resources: [
      'NIST Risk Analysis Template',
      'HIPAA Risk Analysis Guide',
      '§164.308(a)(1)(ii)(A) - REQUIRED'
    ],
    estimatedTime: '2-3 hours (Month 1 portion)',
  });

  tasks.push({
    task: 'Draft Notice of Privacy Practices (NPP)',
    action: 'Customize NPP template for your use case. Describes how you collect, use, and disclose PHI.',
    resources: [
      'Notice of Privacy Practices Template',
      '§164.520 - Privacy Rule'
    ],
    estimatedTime: '2-3 hours',
  });

  return {
    week: 1,
    title: 'Month 1: Foundation (Days 1-30)',
    priority: 'CRITICAL',
    tasks,
  };
}

// MONTH 2: Administrative Safeguards
function generateMonth2(
  size: string,
  orgType: string,
  hasPhysicalFacility: boolean
): RoadmapWeek {
  const tasks: Array<{ task: string; action: string; resources: string[]; estimatedTime: string }> = [];

  // Week 5: Risk analysis
  tasks.push({
    task: 'Complete comprehensive HIPAA risk analysis',
    action: 'Finish risk analysis started in Month 1. Identify all risks, assign severity levels, document mitigation plans.',
    resources: [
      'NIST SP 800-66r2',
      'Risk Analysis Completion Checklist',
      '§164.308(a)(1)(ii)(A) - REQUIRED'
    ],
    estimatedTime: '12-16 hours',
  });

  // Week 6: Policies
  tasks.push({
    task: 'Create workforce security policies',
    action: 'Document policies for: authorization/supervision, workforce clearance, termination procedures.',
    resources: [
      'Workforce Security Policy Template',
      '§164.308(a)(3) - Workforce Security'
    ],
    estimatedTime: '4-6 hours',
  });

  tasks.push({
    task: 'Create information access management policies',
    action: 'Define role-based access controls. Document who has access to what PHI and why (minimum necessary).',
    resources: [
      'Access Management Policy Template',
      '§164.308(a)(4) - Information Access Management'
    ],
    estimatedTime: '4-6 hours',
  });

  // Week 7: Training
  tasks.push({
    task: 'Develop HIPAA security training program',
    action: 'Create training materials covering: PHI handling, password management, breach reporting, physical security.',
    resources: [
      'HIPAA Training Presentation Template',
      'Training Tracking Spreadsheet',
      '§164.308(a)(5) - Security Awareness and Training'
    ],
    estimatedTime: '6-8 hours (creation), 1-2 hours per session',
  });

  tasks.push({
    task: 'Conduct initial security training for all workforce',
    action: 'Train all current employees. Document attendance and acknowledgment. Schedule annual refresher.',
    resources: [
      'Training Acknowledgment Form',
      '§164.308(a)(5)'
    ],
    estimatedTime: '2-3 hours (per session)',
  });

  // Week 7-8: Business associates
  tasks.push({
    task: 'Create Business Associate inventory',
    action: 'List all vendors/contractors with potential PHI access: email (SendGrid), SMS (Twilio), analytics, etc.',
    resources: [
      'Business Associate Inventory Spreadsheet',
      '§164.308(b)'
    ],
    estimatedTime: '3-4 hours',
  });

  tasks.push({
    task: 'Execute BAAs with all business associates',
    action: 'Send BAA requests to all vendors identified in inventory. Track execution status.',
    resources: [
      'BAA Request Email Template',
      'BAA Tracking Spreadsheet'
    ],
    estimatedTime: '15 minutes per vendor (+ vendor processing)',
  });

  // Week 8: Incident response
  tasks.push({
    task: 'Create security incident response plan',
    action: 'Document procedures for: incident detection, assessment, containment, notification (60-day timeline).',
    resources: [
      'Incident Response Plan Template',
      'Breach Notification Timeline Checklist',
      '§164.308(a)(6) - REQUIRED'
    ],
    estimatedTime: '6-8 hours',
  });

  // Week 8: Sanctions
  tasks.push({
    task: 'Create sanction policy for HIPAA violations',
    action: 'Document disciplinary actions for workforce members who violate HIPAA policies.',
    resources: [
      'Sanction Policy Template',
      '§164.308(a)(1)(ii)(C) - REQUIRED'
    ],
    estimatedTime: '2-3 hours',
  });

  if (hasPhysicalFacility) {
    tasks.push({
      task: 'Create facility security plan',
      action: 'Document physical access controls, visitor management, workstation use policies.',
      resources: [
        'Facility Security Plan Template',
        '§164.310(a) - Physical Safeguards'
      ],
      estimatedTime: '4-6 hours',
    });

    tasks.push({
      task: 'Implement workstation security measures',
      action: 'Add privacy screens, automatic screen locks (5 min timeout), position monitors away from public view.',
      resources: [
        'Workstation Security Checklist',
        '§164.310(c) - REQUIRED'
      ],
      estimatedTime: '2-4 hours',
    });
  }

  return {
    week: 2,
    title: 'Month 2: Administrative Safeguards (Days 31-60)',
    priority: 'CRITICAL',
    tasks,
  };
}

// MONTH 3: Technical Safeguards
function generateMonth3(
  cloudProvider: string,
  usesAI: boolean,
  answerMap: Map<string, string | string[]>
): RoadmapWeek {
  const tasks: Array<{ task: string; action: string; resources: string[]; estimatedTime: string }> = [];

  // Week 9: Network segmentation
  const needsSegmentation = answerMap.get('GS25') !== 'no';
  if (needsSegmentation) {
    tasks.push({
      task: 'Implement network segmentation',
      action: cloudProvider === 'aws'
        ? 'Create dedicated VPC for PHI systems. Separate subnets for web, app, database tiers. Configure security groups.'
        : `Create isolated network for PHI systems in ${cloudProvider.toUpperCase()}`,
      resources: [
        cloudProvider === 'aws' ? 'AWS VPC Best Practices' : `${cloudProvider.toUpperCase()} Network Guide`,
        'Network Architecture Diagram Template',
        '2026 HIPAA Update - Network Segmentation REQUIRED'
      ],
      estimatedTime: '6-8 hours',
    });
  }

  // Week 9-10: Encryption in transit
  tasks.push({
    task: 'Enforce TLS 1.2+ for all connections',
    action: 'Configure load balancers, API gateways to require TLS 1.2 minimum. Disable older protocols.',
    resources: [
      cloudProvider === 'aws' ? 'AWS ALB TLS Configuration' : 'TLS Configuration Guide',
      '§164.312(e)(2)(ii) - Transmission Security'
    ],
    estimatedTime: '3-4 hours',
  });

  // Week 10: Automatic logoff
  const needsLogoff = answerMap.get('GS24') !== 'no';
  if (needsLogoff) {
    tasks.push({
      task: 'Implement automatic session timeout',
      action: 'Configure 15-minute session timeout for all applications. Implement "remember me" exception with re-authentication.',
      resources: [
        'Session Management Best Practices',
        '§164.312(a)(2)(iii) - Automatic Logoff (Addressable)'
      ],
      estimatedTime: '4-6 hours',
    });
  }

  // Week 10-11: Unique user IDs
  tasks.push({
    task: 'Ensure unique user IDs for all users',
    action: 'Audit user accounts. Eliminate any shared accounts. Enforce username uniqueness.',
    resources: [
      'User Account Audit Checklist',
      '§164.312(a)(2)(i) - Unique User ID (REQUIRED)'
    ],
    estimatedTime: '2-4 hours',
  });

  // Week 11: AI logging (if applicable)
  if (usesAI) {
    tasks.push({
      task: 'Implement AI prompt/response logging',
      action: cloudProvider === 'aws'
        ? 'Enable CloudTrail logging for Bedrock API calls. Configure CloudWatch Logs with 6-year retention.'
        : 'Configure logging for all AI API calls with 6-year retention.',
      resources: [
        cloudProvider === 'aws' ? 'AWS Bedrock Logging Guide' : 'AI Logging Setup Guide',
        '§164.312(b) - Audit Controls for AI'
      ],
      estimatedTime: '3-4 hours',
    });

    tasks.push({
      task: 'Verify AI vendors won\'t train on your PHI',
      action: 'Review vendor contracts for no-training clauses. Enable zero-retention settings (if available). Document contractual protections.',
      resources: [
        'AI Vendor Contract Review Checklist',
        'Zero-Retention Configuration Guide'
      ],
      estimatedTime: '2-3 hours',
    });
  }

  // Week 11-12: Data integrity
  tasks.push({
    task: 'Implement data integrity controls',
    action: 'Enable versioning (S3), checksums (file uploads), digital signatures (critical documents).',
    resources: [
      cloudProvider === 'aws' ? 'S3 Versioning Guide' : 'Data Integrity Guide',
      '§164.312(c)(1) - Integrity (Addressable)'
    ],
    estimatedTime: '3-4 hours',
  });

  // Week 12: Contingency plan
  tasks.push({
    task: 'Create and test data backup procedures',
    action: 'Configure automated daily backups with 30-day retention. Test restoration process. Document procedures.',
    resources: [
      'Backup Configuration Guide',
      'Restoration Testing Checklist',
      '§164.308(a)(7)(ii)(A) - REQUIRED'
    ],
    estimatedTime: '6-8 hours',
  });

  tasks.push({
    task: 'Create disaster recovery plan',
    action: 'Document recovery time objectives (RTO), recovery point objectives (RPO). Create runbook for system restoration.',
    resources: [
      'Disaster Recovery Plan Template',
      '§164.308(a)(7)(ii)(B) - REQUIRED'
    ],
    estimatedTime: '6-8 hours',
  });

  tasks.push({
    task: 'Test disaster recovery procedures',
    action: 'Simulate disaster scenario. Execute recovery plan. Document time to recovery. Update plan based on learnings.',
    resources: [
      'DR Testing Checklist',
      '§164.308(a)(7)(ii)(D) - Testing (Addressable)'
    ],
    estimatedTime: '4-6 hours',
  });

  return {
    week: 3,
    title: 'Month 3: Technical Safeguards (Days 61-90)',
    priority: 'HIGH',
    tasks,
  };
}

// MONTH 4: Documentation & Audit Prep
function generateMonth4(
  approach: string,
  budget: string,
  orgType: string
): RoadmapWeek {
  const tasks: Array<{ task: string; action: string; resources: string[]; estimatedTime: string }> = [];

  // Week 13: Policy consolidation
  tasks.push({
    task: 'Consolidate all policies into HIPAA Security Policy Manual',
    action: 'Combine all policies created in Months 1-3 into single comprehensive manual. Add table of contents, version control.',
    resources: [
      'HIPAA Policy Manual Template',
      'Policy Index Template'
    ],
    estimatedTime: '6-8 hours',
  });

  tasks.push({
    task: 'Create termination procedures checklist',
    action: 'Document step-by-step process for revoking access when employee leaves: disable accounts, retrieve devices, exit interview.',
    resources: [
      'Termination Checklist Template',
      '§164.308(a)(3)(ii)(C) - Termination Procedures'
    ],
    estimatedTime: '2-3 hours',
  });

  // Week 14: Evaluation
  tasks.push({
    task: 'Conduct periodic technical evaluation',
    action: 'Review all technical controls implemented. Verify encryption, logging, MFA, network segmentation are working correctly.',
    resources: [
      'Technical Evaluation Checklist',
      '§164.308(a)(8) - Evaluation (REQUIRED)'
    ],
    estimatedTime: '6-8 hours',
  });

  tasks.push({
    task: 'Conduct periodic non-technical evaluation',
    action: 'Review all policies, training documentation, BAAs. Verify workforce compliance with policies.',
    resources: [
      'Non-Technical Evaluation Checklist',
      '§164.308(a)(8) - Evaluation (REQUIRED)'
    ],
    estimatedTime: '4-6 hours',
  });

  // Week 15: Mock audit
  tasks.push({
    task: 'Conduct mock HIPAA audit',
    action: 'Use HHS audit protocol. Review all documentation. Identify gaps. Document findings and remediation plans.',
    resources: [
      'HHS Audit Protocol 2024',
      'Mock Audit Checklist',
      'Gap Remediation Template'
    ],
    estimatedTime: '8-12 hours',
  });

  tasks.push({
    task: 'Organize evidence repository',
    action: 'Create folder structure for audit evidence: policies, training records, BAAs, risk analysis, logs, testing results.',
    resources: [
      'Evidence Repository Structure Template',
      'Audit Readiness Checklist'
    ],
    estimatedTime: '4-6 hours',
  });

  // Week 16: Final prep
  tasks.push({
    task: 'Review and update risk management plan',
    action: 'Review Month 1 risk analysis. Update with risks identified during implementation. Document mitigation for all high-priority risks.',
    resources: [
      'Risk Management Plan Template',
      '§164.308(a)(1)(ii)(B) - REQUIRED'
    ],
    estimatedTime: '4-6 hours',
  });

  if (approach === 'diy') {
    tasks.push({
      task: 'Schedule quarterly compliance reviews',
      action: 'Set up recurring calendar events for quarterly reviews of policies, training, access logs, and risk analysis updates.',
      resources: [
        'Quarterly Review Agenda Template',
        'Compliance Maintenance Checklist'
      ],
      estimatedTime: '1 hour setup',
    });
  } else {
    tasks.push({
      task: 'Evaluate compliance automation platforms',
      action: 'Review platforms like Infinitra, Vanta, Drata for ongoing automated compliance monitoring and evidence collection.',
      resources: [
        'Compliance Platform Comparison',
        'ROI Calculator for Compliance Automation'
      ],
      estimatedTime: '4-6 hours',
    });
  }

  return {
    week: 4,
    title: 'Month 4: Documentation & Audit Prep (Days 91-120)',
    priority: 'MEDIUM',
    tasks,
  };
}

// Helper functions
function calculateEstimatedTime(totalItems: number, size: string, approach: string): string {
  let baseHours = totalItems * 3; // Average 3 hours per item

  // Adjust for org size
  if (size === 'solo' || size === 'small') {
    baseHours *= 1.3; // Smaller teams take longer (less parallelization)
  } else if (size === 'large') {
    baseHours *= 0.7; // Larger teams can parallelize
  }

  // Adjust for approach
  if (approach === 'done') {
    baseHours *= 0.3; // Consultant does most work
  } else if (approach === 'guided') {
    baseHours *= 0.7; // Guidance accelerates
  }

  return `${Math.round(baseHours)}-${Math.round(baseHours * 1.3)} hours total across your team`;
}

function calculateEstimatedCost(budget: string, cloudProvider: string, usesAI: boolean): string {
  if (budget === 'minimal') {
    return '$500-1,000/month (AWS costs only)';
  } else if (budget === 'standard') {
    const base = usesAI ? '$800-1,500' : '$500-1,000';
    return `${base}/month (cloud + tools)`;
  } else if (budget === 'platform') {
    return '$2,000-4,000/month (cloud + compliance platform like Infinitra)';
  } else if (budget === 'full') {
    return '$25k-50k one-time (implementation) + $2k-4k/month (platform)';
  }
  return '$500-2,000/month';
}

function getRecommendedApproach(
  timeline: string,
  size: string,
  budget: string,
  approach: string
): string {
  if (timeline === '3months' && approach === 'diy') {
    return 'RECOMMENDATION: With a 3-month timeline, consider hiring expert help (guided or done-for-you) to accelerate implementation.';
  } else if (timeline === 'flexible' && budget === 'minimal') {
    return 'RECOMMENDATION: With a flexible timeline and minimal budget, DIY approach using your 4-month plan is perfect.';
  } else if (size === 'solo' && approach === 'diy') {
    return 'RECOMMENDATION: As a solo founder, consider guided approach to avoid compliance overwhelm while building product.';
  } else if (budget === 'full' && approach === 'diy') {
    return 'RECOMMENDATION: With full budget allocated, done-for-you implementation will get you audit-ready fastest (6-8 weeks vs. 4 months).';
  }
  return `Your ${approach} approach matches your timeline and budget well. Follow the 4-month plan at your own pace.`;
}

function getResources(
  cloudProvider: string,
  usesAI: boolean,
  hasPhysicalFacility: boolean
): { templates: string[]; guides: string[]; tools: string[] } {
  const templates = [
    'HIPAA Risk Analysis Template (NIST)',
    'Business Associate Agreement Template',
    'Notice of Privacy Practices Template',
    'Incident Response Plan Template',
    'Security Policy Manual Template',
    'Training Acknowledgment Forms',
    'Termination Checklist',
  ];

  if (hasPhysicalFacility) {
    templates.push('Facility Security Plan Template', 'Visitor Log Template');
  }

  const guides = [
    'NIST SP 800-66r2 (Official HIPAA Implementation Guide)',
    'HHS HIPAA Audit Protocol 2024',
    '45 CFR Part 164 (HIPAA Regulations)',
  ];

  if (cloudProvider === 'aws') {
    guides.push(
      'AWS HIPAA Compliance Whitepaper',
      'AWS KMS Encryption Guide',
      'AWS CloudTrail Setup Guide',
      'AWS VPC Best Practices'
    );
  } else if (cloudProvider === 'azure') {
    guides.push(
      'Azure HIPAA Compliance Guide',
      'Azure Key Vault Setup',
      'Azure Monitor Configuration'
    );
  } else if (cloudProvider === 'gcp') {
    guides.push(
      'GCP HIPAA Compliance Guide',
      'GCP Cloud KMS Setup',
      'GCP Audit Logging'
    );
  }

  if (usesAI) {
    guides.push(
      'AI/ML HIPAA Compliance Guide',
      'State AI Laws Compliance (CA, CO, NY, IL)'
    );
  }

  const tools = [
    'CloudFormation/Terraform Templates (Infrastructure as Code)',
    'Risk Analysis Spreadsheet',
    'Business Associate Inventory Tracker',
  ];

  if (cloudProvider === 'aws') {
    tools.push('AWS Config (Compliance monitoring)', 'AWS Security Hub');
  }

  return { templates, guides, tools };
}
